import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Field, Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";

import { clsxMerge } from "shared/lib/helpers";
import { TextField } from "shared/ui/user-input";
import { inputBadgesToString } from "shared/ui/user-input/badge-input/helper";
import { BadgeI, BadgeValidationI } from "shared/ui/user-input/badge-input";
import { EmailEditor, EmailEditorContentI } from "shared/ui/email/editor";
import { AttachmentI, EmailTemplateI } from "@/api/routes/email";
import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { useValidateEmails } from "@/components/modules/email-templates-sidebar/queries";
import { EmailAttachmentsSection } from "@/components/modules/email-templates-sidebar/shared/email-attachments-section";
import { EmailRecipientsSection } from "./recipients-section";

import { EmailFormActionsSection as DefaultActionsSection } from "./actions-section";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import {
  EMAIL_TEMPLATE_UNVERIFIED_CONFIRM_MODAL_ID,
  EmailTemplateUnverifiedConfirmModal,
} from "@/components/modals/email-template-unverified-confirm-modal";
import { Portal } from "react-portal";
import {
  EmailFollowUpsI,
  EmailFormFormattedDataI,
} from "@/components/modules/email-templates-sidebar/interfaces";
import { EmailFormSkeleton } from "./email-form-skeleton";
import { DelayedComponent } from "@/components/base/delayed-component";
import striptags from "striptags";

const createLabelValuePair = (label: string, value: string) => ({
  label,
  value,
});

interface EmailFormPropsI {
  template: EmailTemplateI;
  senderEmail?: string;

  initialSubject?: string;
  initialBody?: string;
  initialRecipientsTo?: BadgeI[] | string[];
  initialRecipientsCc?: BadgeI[] | string[];
  initialRecipientsBcc?: BadgeI[] | string[];
  initialAttachments?: AttachmentI[];

  attachmentOptions?: AttachmentI[];

  isAIGenerationComplete?: boolean;
  isAIGenerating?: boolean;
  isSendingEmail?: boolean;
  isSendEmailSuccess?: boolean;

  //TODO deprecate and remove
  hideTitle?: boolean;
  hideIntro?: boolean;
  hideAiGenerateButton?: boolean;
  hideRecipientsSection?: boolean;

  hideAttachmentSection?: boolean;

  isDisabledSubjectSection?: boolean;

  /**
   * Allows for a custom actions section to replace the default one.
   * Note: When implementing a custom actions section, exercise caution with the type="submit" attribute.
   * Using both a custom submit handler and type="submit" will trigger duplicate form submissions,
   * as the form will be submitted through both mechanisms simultaneously.
   */
  actionsSection?:
    | React.ReactNode
    | ((props: { submitForm: () => void }) => React.ReactNode);

  onComposeWithAI?: () => void;
  onChangeSubject?: (subject?: string) => void;
  onChangeBody?: (body?: string) => void;
  onChangeFollowUps?: (emailFollowUps: EmailFollowUpsI) => void;
  onChangeAttachments?: (attachments: AttachmentI[]) => void;

  onChangeRecipientsTo?: (to: BadgeI[]) => void;
  onChangeRecipientsCc?: (cc: BadgeI[]) => void;
  onChangeRecipientsBcc?: (bcc: BadgeI[]) => void;

  onSend?: (
    data: EmailFormFormattedDataI
  ) => Promise<boolean | void> | undefined;
}

export interface EmailFormI {
  to: BadgeI[] | string[];
  cc?: BadgeI[] | string[];
  bcc?: BadgeI[] | string[];
  subject: string;
  //@deprecated
  // max_follow_ups: number;
  // follow_up_interval_days: number;
}

const EnhancedTextField = TextField(Field);

const EmailFormValidation = Yup.object().shape({
  to: Yup.array().required("Required field").min(1, "At least 1 is required"),
  cc: Yup.array(),
  bcc: Yup.array(),
  subject: Yup.string()
    .trim()
    .required("Required field")
    .max(500, "Subject cannot exceed 500 characters"),
});

const TrackChangesAndUpdateState = ({
  value,
  setState,
}: {
  value?: any;
  setState?: (value: any) => void;
}) => {
  useEffect(() => {
    setState?.(value);
  }, [value]);

  return null;
};

/**
 * Form is resused accross different parts of account details
 *
 * @description This component is used to compose an email.
 * @param param0
 * @returns
 */
export const EmailForm: FC<EmailFormPropsI> = ({
  template,
  senderEmail,

  initialRecipientsTo,
  initialRecipientsCc,
  initialRecipientsBcc,
  initialSubject,
  initialBody,
  initialAttachments = [],

  attachmentOptions = [],

  isAIGenerationComplete,
  isAIGenerating,
  isSendingEmail,
  isSendEmailSuccess,

  hideTitle,
  hideIntro,
  hideAiGenerateButton,
  hideRecipientsSection,
  hideAttachmentSection,

  isDisabledSubjectSection,

  actionsSection,
  onChangeBody,
  onChangeSubject,

  onChangeAttachments,
  onChangeRecipientsTo,
  onChangeRecipientsCc,
  onChangeRecipientsBcc,

  onSend = async () => false,
}) => {
  const { focusedContact } = useAccountDetailsContext();
  const { mutateAsync: validateEmailsAsync } = useValidateEmails();
  const [isEmailsValid, setIsEmailsValid] = useState(false);
  const formikRef = useRef<FormikProps<EmailFormI>>(null);

  const [bodyContent, setBodyContent] = useState<EmailEditorContentI>({
    htmlText: initialBody || "",
  });
  const [bodyError, setBodyError] = useState<string>();

  const [selectedAttachments, setSelectedAttachments] = useState<AttachmentI[]>(
    initialAttachments || template.attachments || []
  );

  // the "to" field uses <BadgeInput />, so the default value for the "to" field needs to be type BadgeI
  const form: EmailFormI = useMemo(
    () => ({
      to: initialRecipientsTo || [
        createLabelValuePair(
          focusedContact?.email || "",
          focusedContact?.email || ""
        ),
      ],
      cc:
        initialRecipientsCc?.map((email) => {
          if (typeof email === "string") {
            return createLabelValuePair(email, email);
          }

          return email;
        }) || [],
      bcc:
        initialRecipientsBcc?.map((email) => {
          if (typeof email === "string") {
            return { label: email, value: email };
          }

          return email;
        }) || [],
      subject: initialSubject || "",
    }),
    [focusedContact, initialRecipientsCc]
  );

  const handleEmailValidation = async (emails: string[]) => {
    const resp = await validateEmailsAsync(emails);

    const validationData: BadgeValidationI = {
      validValues: [],
      invalidValues: [],
    };

    if (resp.status === 200) {
      validationData.validValues = resp.data.valid_emails;
      validationData.invalidValues = resp.data.invalid_emails;
    }

    setIsEmailsValid(
      validationData.invalidValues.length === 0 &&
        validationData.validValues.length > 0
    );

    return validationData;
  };

  const handleAttachmentsChange = (attachment: AttachmentI) => {
    let updatedAttachments = selectedAttachments;

    if (selectedAttachments.filter((v) => v.id === attachment.id).length) {
      updatedAttachments = updatedAttachments.filter(
        (v) => v.id !== attachment.id
      );
    } else {
      updatedAttachments.push(attachment);
    }

    const attachments = [...updatedAttachments];

    onChangeAttachments?.(attachments);
    setSelectedAttachments(attachments);
  };

  const handleOnChangeBody = (content: EmailEditorContentI) => {
    setBodyError(undefined);
    setBodyContent(content);
  };

  const onFormSubmitHandler = async (formData: EmailFormI) => {
    setBodyError(undefined);

    const htmlText = bodyContent?.htmlText || "";
    const plainText = striptags(htmlText || "");

    if (!htmlText?.trim() || !plainText?.trim()) {
      setBodyError("Email body is required");
      return;
    }

    onSend({
      to: inputBadgesToString(formData.to),
      cc: inputBadgesToString(formData.cc),
      bcc: inputBadgesToString(formData.bcc),
      subject: formData.subject,
      html_body: htmlText,
      plain_body: plainText,

      attachments: selectedAttachments.map((v) => v.id),
    });
  };

  useEffect(() => {
    formikRef.current?.setFieldValue("subject", initialSubject || "");
  }, [initialSubject]);

  const handleSubmit = (submitForm: () => void) => () => {
    if (!isEmailsValid) {
      modalHelpers.trigger(EMAIL_TEMPLATE_UNVERIFIED_CONFIRM_MODAL_ID);
    } else {
      submitForm();
    }
  };

  useEffect(() => {
    setBodyContent({
      htmlText: initialBody,
    });
  }, [initialBody]);

  useEffect(() => {
    onChangeBody?.(bodyContent?.htmlText);
  }, [bodyContent?.htmlText]);

  const isCustomPlaceHolder = useMemo(() => {
    if (bodyContent?.plainText && bodyContent?.plainText?.length > 5) {
      return false;
    }

    return striptags(bodyContent?.htmlText || "") === "";
  }, [bodyContent?.htmlText, bodyContent?.plainText]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={form}
      validationSchema={EmailFormValidation}
      onSubmit={onFormSubmitHandler}
      enableReinitialize
    >
      {({ errors, touched, submitForm, values }) => (
        <Form className="flex h-full flex-col justify-between">
          <TrackChangesAndUpdateState
            value={values?.subject}
            setState={onChangeSubject}
          />

          <TrackChangesAndUpdateState
            value={values?.to}
            setState={onChangeRecipientsTo}
          />

          <TrackChangesAndUpdateState
            value={values?.cc}
            setState={onChangeRecipientsCc}
          />

          <TrackChangesAndUpdateState
            value={values?.bcc}
            setState={onChangeRecipientsBcc}
          />

          <section>
            {!hideIntro && (
              <div className="mb-5">
                {!hideTitle && (
                  <div className="mb-3 typography-header-6">
                    {template.name}
                  </div>
                )}

                <div className="mb-2 typography-body-4-medium">
                  <span className="text-gradient">Compose with AI</span> will
                  generate an email with the following prompt:
                </div>

                <div className="rounded-lg bg-white px-3 py-2 italic typography-body-4">
                  {template.prompt}
                </div>
              </div>
            )}

            {!hideRecipientsSection && (
              <EmailRecipientsSection
                className={clsxMerge({ "-mb-6": hideAiGenerateButton })}
                senderEmail={senderEmail}
                to={initialRecipientsTo}
                cc={initialRecipientsCc}
                bcc={initialRecipientsBcc}
                errors={errors}
                touched={touched}
                onValidateEmails={handleEmailValidation}
                disabled={isAIGenerating}
                isDefaultCcVisible={!!initialRecipientsCc?.length}
                isDefaultBccVisible={!!initialRecipientsBcc?.length}
              />
            )}
            <div className={"mb-5"}>
              <div
                className={clsxMerge("mb-2 flex items-center justify-between")}
              >
                <h6 className="typography-body-4-medium">Subject</h6>
              </div>

              <EnhancedTextField
                name="subject"
                placeholder="Just following up on my last call"
                inputClassName={clsxMerge(
                  "border-[#ccc] bg-white typography-body-4-medium placeholder:text-[#ccc]",
                  isAIGenerating && "bg-black/5"
                )}
                errors={errors.subject}
                touched={touched.subject}
                disabled={isDisabledSubjectSection || isAIGenerating}
              />
            </div>

            <div className="mb-5">
              <h6 className="mb-2 typography-body-4-medium">Body</h6>

              {isAIGenerating && !isAIGenerationComplete && (
                <EmailFormSkeleton className="p-0" />
              )}

              {!isAIGenerating && (
                // Delay the component to avoid flickering and infinite draft updates
                <DelayedComponent delay={300}>
                  {({ isReady }) =>
                    isReady ? (
                      <div className="relative">
                        {isCustomPlaceHolder && (
                          <span className="pointer-events-none absolute left-4 top-[57px] text-[#ccc] typography-body-4">
                            Dear prospect ...
                          </span>
                        )}
                        <EmailEditor
                          className={clsxMerge(
                            "min-h-[200px] border border-[#ccc]",
                            "animate-fadein",
                            {
                              "bg-black/5": isAIGenerating,
                            }
                          )}
                          placeholder=""
                          onChange={handleOnChangeBody}
                          initialContent={initialBody || ""}
                          content={bodyContent?.htmlText || ""}
                          error={bodyError}
                          disabled={isAIGenerating}
                        />
                      </div>
                    ) : null
                  }
                </DelayedComponent>
              )}
            </div>

            {!hideAttachmentSection && (
              <EmailAttachmentsSection
                attachmentOptions={attachmentOptions}
                selectedAttachments={selectedAttachments}
                onChange={handleAttachmentsChange}
              />
            )}
          </section>

          {(actionsSection &&
            (typeof actionsSection === "function"
              ? actionsSection({ submitForm: handleSubmit(submitForm) })
              : actionsSection)) || (
            <DefaultActionsSection
              isSendingEmail={isSendingEmail}
              isSendEmailSuccess={isSendEmailSuccess}
              isEmailsValid={isEmailsValid}
              isAIGenerating={isAIGenerating}
              onSubmit={handleSubmit(submitForm)}
            />
          )}

          {/*@ts-ignore*/}
          <Portal>
            <EmailTemplateUnverifiedConfirmModal
              email={inputBadgesToString(values.to)[0]}
              onConfirm={submitForm}
            />
          </Portal>
        </Form>
      )}
    </Formik>
  );
};
